import React from 'react';
import { TouchableOpacity } from 'react-native';

import { Stack } from '@/components/layout';
import {
  Button,
  Checkbox,
  SheetContent,
  SheetModal,
  SheetModalTrigger,
  SvgIcon,
  Text,
} from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { createStyles, useScaling } from '@/hooks';

import { colors } from '@/theme/colors';
import { usePlayerFilters, type PlayerLastActiveFilter } from '../context/player-filters-context';

export type PlayerFilterSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filterButtonLabel: string;
  closeWithoutSave: () => void;
  saveAndClose: () => void;
  levels: number[];
  draftLevel: number | null;
  onDraftLevelChange: (level: number | null) => void;
  lastActiveOptions: Array<{ key: PlayerLastActiveFilter; label: string }>;
  draftLastActive: PlayerLastActiveFilter | null;
  onDraftLastActiveChange: (value: PlayerLastActiveFilter | null) => void;
};

export function PlayerFilterSheet({
  open,
  onOpenChange,
  filterButtonLabel,
  closeWithoutSave,
  saveAndClose,
  levels,
  draftLevel,
  onDraftLevelChange,
  lastActiveOptions,
  draftLastActive,
  onDraftLastActiveChange,
}: PlayerFilterSheetProps) {
  const { level } = usePlayerFilters();
  const styles = useStyles();
  const { scale, deviceWidth, deviceHeight, isTablet } = useScaling();
  const isLandscape = deviceWidth > deviceHeight;

  /** Last Active: single selection — choosing one option clears the previous; ignore uncheck toggle on same row. */
  const onLastActiveOptionChange = (key: PlayerLastActiveFilter, checked: boolean) => {
    if (checked) {
      onDraftLastActiveChange(key);
    }
  };

  return (
    <SheetModal open={open} onOpenChange={onOpenChange}>
      <SheetModalTrigger>
        <Button style={styles.filterButton}>
          <Button.Icon name="IcFilter" size={24} />
          <Button.Text variant="Font Inter/Subtitle/Regular 12px" style={styles.filterButtonText}>
            {filterButtonLabel}
            <Text fontWeight="700">{level && STRINGS.player.filters.buttonWithLevel(level)}</Text>
          </Button.Text>
        </Button>
      </SheetModalTrigger>

      <SheetContent tabletWidth={scale(375)} closeOnBackdropPress={false} style={styles.sheet}>
        <Stack direction="column" style={styles.sheetContainer}>
          <Stack direction="row" align="center" style={styles.sheetHeader}>
            <TouchableOpacity
              onPress={closeWithoutSave}
              hitSlop={scale(12)}
              style={styles.backButton}>
              <Stack direction="row" align="center" gap={scale(2)}>
                <SvgIcon name="IcBackBlue" size={24} />
                <Text
                  variant={
                    isTablet ? 'Font Inter/Subtitle/Regular 12px' : 'Font Inter/Subtitle/Medium'
                  }
                  style={styles.backText}>
                  {STRINGS.player.filters.back}
                </Text>
              </Stack>
            </TouchableOpacity>

            <Stack flex={1} align="center">
              <Text
                variant={
                  isTablet
                    ? 'Font Inter/Subtitle/Bold 14px'
                    : 'Font Montserrat/Subtitle/Minimize 1 Bold 11px'
                }
                fontWeight={isTablet ? undefined : '800'}
                style={styles.title}>
                {STRINGS.player.filters.sheetTitle}
              </Text>
            </Stack>

            <Stack style={styles.headerRightSpacer} />
          </Stack>

          {isLandscape ? (
            <>
              <Stack style={styles.sheetBodyContent}>
                <Text
                  variant={
                    isTablet
                      ? 'Font Inter/Subtitle/Bold 12px'
                      : 'Font Montserrat/Subtitle/Minimize 1 Bold 11px'
                  }
                  fontWeight={isTablet ? undefined : '800'}
                  style={styles.section}>
                  {STRINGS.player.filters.sectionByLevel}
                </Text>

                <Stack direction="row" gap={scale(24)} style={styles.twoCols}>
                  <Stack direction="column" gap={scale(16)} flex={1}>
                    {levels.slice(0, 5).map(n => (
                      <Checkbox
                        key={`level-${n}`}
                        label={STRINGS.player.filters.levelLabel(n)}
                        checked={draftLevel === n}
                        onValueChange={checked => onDraftLevelChange(checked ? n : null)}
                        style={styles.checkboxRow}
                      />
                    ))}
                  </Stack>

                  <Stack direction="column" gap={scale(16)} flex={1}>
                    {levels.slice(5).map(n => (
                      <Checkbox
                        key={`level-${n}`}
                        label={STRINGS.player.filters.levelLabel(n)}
                        checked={draftLevel === n}
                        onValueChange={checked => onDraftLevelChange(checked ? n : null)}
                        style={styles.checkboxRow}
                      />
                    ))}
                  </Stack>
                </Stack>

                <Text
                  variant={
                    isTablet
                      ? 'Font Inter/Subtitle/Bold 12px'
                      : 'Font Montserrat/Subtitle/Minimize 1 Bold 11px'
                  }
                  fontWeight={isTablet ? undefined : '800'}
                  style={[styles.section, styles.sectionSpacing]}>
                  {STRINGS.player.filters.sectionByLastActive}
                </Text>

                {isTablet ? (
                  <Stack direction="column" gap={scale(16)}>
                    {lastActiveOptions.map(opt => (
                      <Checkbox
                        key={opt.key}
                        label={opt.label}
                        checked={draftLastActive === opt.key}
                        onValueChange={checked => onLastActiveOptionChange(opt.key, checked)}
                        style={styles.checkboxRow}
                      />
                    ))}
                  </Stack>
                ) : (
                  <Stack direction="row" gap={scale(24)} style={styles.twoCols}>
                    <Stack direction="column" gap={scale(16)} flex={1}>
                      {lastActiveOptions
                        .filter(
                          opt =>
                            opt.key === '1_month' ||
                            opt.key === '6_months' ||
                            opt.key === 'show_all',
                        )
                        .map(opt => (
                          <Checkbox
                            key={opt.key}
                            label={opt.label}
                            checked={draftLastActive === opt.key}
                            onValueChange={checked => onLastActiveOptionChange(opt.key, checked)}
                            style={styles.checkboxRow}
                          />
                        ))}
                    </Stack>

                    <Stack direction="column" gap={scale(16)} flex={1}>
                      {lastActiveOptions
                        .filter(opt => opt.key === '3_months' || opt.key === '1_5_years')
                        .map(opt => (
                          <Checkbox
                            key={opt.key}
                            label={opt.label}
                            checked={draftLastActive === opt.key}
                            onValueChange={checked => onLastActiveOptionChange(opt.key, checked)}
                            style={styles.checkboxRow}
                          />
                        ))}
                    </Stack>
                  </Stack>
                )}
              </Stack>
              <Stack flex={1} />
              <Stack style={styles.sheetFooterLandscape}>
                <Button onPress={saveAndClose} style={styles.doneButton}>
                  <Button.Text
                    variant={
                      isTablet
                        ? 'Font Inter/Subtitle/Medium 14px'
                        : 'Font Inter/Subtitle/Regular 12px'
                    }>
                    {STRINGS.player.filters.done}
                  </Button.Text>
                </Button>
              </Stack>
            </>
          ) : (
            <Stack style={styles.sheetBodyContent}>
              <Text
                variant={
                  isTablet
                    ? 'Font Inter/Subtitle/Bold 12px'
                    : 'Font Montserrat/Subtitle/Minimize 1 Bold 11px'
                }
                fontWeight={isTablet ? undefined : '800'}
                style={styles.section}>
                {STRINGS.player.filters.sectionByLevel}
              </Text>

              <Stack direction="row" gap={scale(24)} style={styles.twoCols}>
                <Stack direction="column" gap={scale(16)} flex={1}>
                  {levels.slice(0, 5).map(n => (
                    <Checkbox
                      key={`level-${n}`}
                      label={STRINGS.player.filters.levelLabel(n)}
                      checked={draftLevel === n}
                      onValueChange={checked => onDraftLevelChange(checked ? n : null)}
                      style={styles.checkboxRow}
                    />
                  ))}
                </Stack>

                <Stack direction="column" gap={scale(16)} flex={1}>
                  {levels.slice(5).map(n => (
                    <Checkbox
                      key={`level-${n}`}
                      label={STRINGS.player.filters.levelLabel(n)}
                      checked={draftLevel === n}
                      onValueChange={checked => onDraftLevelChange(checked ? n : null)}
                      style={styles.checkboxRow}
                    />
                  ))}
                </Stack>
              </Stack>

              <Text
                variant={
                  isTablet
                    ? 'Font Inter/Subtitle/Bold 12px'
                    : 'Font Montserrat/Subtitle/Minimize 1 Bold 11px'
                }
                fontWeight={isTablet ? undefined : '800'}
                style={[styles.section, styles.sectionSpacing]}>
                {STRINGS.player.filters.sectionByLastActive}
              </Text>

              {isTablet ? (
                <Stack direction="column" gap={scale(16)}>
                  {lastActiveOptions.map(opt => (
                    <Checkbox
                      key={opt.key}
                      label={opt.label}
                      checked={draftLastActive === opt.key}
                      onValueChange={checked => onLastActiveOptionChange(opt.key, checked)}
                      style={styles.checkboxRow}
                    />
                  ))}
                </Stack>
              ) : (
                <Stack direction="row" gap={scale(24)} style={styles.twoCols}>
                  <Stack direction="column" gap={scale(16)} flex={1}>
                    {lastActiveOptions
                      .filter(
                        opt =>
                          opt.key === '1_month' || opt.key === '6_months' || opt.key === 'show_all',
                      )
                      .map(opt => (
                        <Checkbox
                          key={opt.key}
                          label={opt.label}
                          checked={draftLastActive === opt.key}
                          onValueChange={checked => onLastActiveOptionChange(opt.key, checked)}
                          style={styles.checkboxRow}
                        />
                      ))}
                  </Stack>

                  <Stack direction="column" gap={scale(16)} flex={1}>
                    {lastActiveOptions
                      .filter(opt => opt.key === '3_months' || opt.key === '1_5_years')
                      .map(opt => (
                        <Checkbox
                          key={opt.key}
                          label={opt.label}
                          checked={draftLastActive === opt.key}
                          onValueChange={checked => onLastActiveOptionChange(opt.key, checked)}
                          style={styles.checkboxRow}
                        />
                      ))}
                  </Stack>
                </Stack>
              )}

              <Stack style={styles.sheetFooterPortrait}>
                <Button onPress={saveAndClose} style={styles.doneButton}>
                  <Button.Text>{STRINGS.player.filters.done}</Button.Text>
                </Button>
              </Stack>
            </Stack>
          )}
        </Stack>
      </SheetContent>
    </SheetModal>
  );
}

const useStyles = createStyles(({ scale, isTablet }) => ({
  filterButtonText: {
    textTransform: 'uppercase',
    paddingHorizontal: scale(0),
    paddingVertical: scale(0),
  },
  filterButton: {
    height: scale(!isTablet ? 32 : 40),
    flex: !isTablet ? 1 : undefined,
    width: isTablet ? scale(140) : undefined,
    borderRadius: scale(4),
    paddingHorizontal: scale(0),
    paddingVertical: scale(0),
  },
  sheet: {
    backgroundColor: 'white',
  },
  sheetContainer: {
    flex: 1,
  },
  sheetHeader: {
    paddingHorizontal: scale(20),
    paddingTop: scale(35),
    marginBottom: scale(40),
  },
  backButton: {
    width: scale(45),
    alignItems: 'center',
    justifyContent: 'center',
  },
  backText: {
    color: colors.Blue,
    textTransform: 'uppercase',
  },
  title: {
    color: colors.Blue,
    textTransform: 'uppercase',
  },
  headerRightSpacer: {
    width: scale(45),
  },
  sheetBody: {
    flex: 1,
  },
  sheetBodyContent: {
    paddingHorizontal: scale(20),
    paddingBottom: scale(16),
  },
  section: {
    color: colors.Blue,
    textTransform: 'uppercase',
    marginBottom: scale(12),
  },
  sectionSpacing: {
    marginTop: scale(32),
  },
  twoCols: {
    width: '100%',
  },
  checkboxRow: {},
  sheetFooterPortrait: {
    // paddingBottom: scale(16),
    paddingTop: scale(32),
  },
  sheetFooterLandscape: {
    paddingHorizontal: scale(16),
    // paddingBottom: scale(20),
    paddingTop: scale(8),
    justifyContent: 'flex-end',
  },
  doneButton: {
    width: '100%',
  },
}));
